export class ProductPost extends window.BAO.CustomElement() {
  setupListeners () {
    super.setupListeners()

    this.listeners.add(document.body, 'change', this.onChangeHandler.bind(this))

    if (document.querySelector('[data-product-post-el="post-radio"]:checked')) {
      requestAnimationFrame(() => this.updatePrice())
    }

    this.updateSubmitButton()
  }

  /**
   * @param {Event} event
   */
  onChangeHandler (event) {
    const { target } = event

    if (target.matches('[data-product-post-el="post-radio"]')) {
      this.onPostSelected(target)
    } else if (target.matches('[data-product-post-el="size-radio"]')) {
      this.onSizeSelected(target)
    }
  }

  /**
   * @param {HTMLInputElement} radio
   */
  onPostSelected (radio) {
    const postProductId = radio.value
    const postTitle = radio.dataset.postTitle
    const postInitial = radio.dataset.postInitial
    const postImage = radio.dataset.postImage

    const {
      'post-label': postLabel,
      'post-label-value': postLabelValue,
      'post-icon': postIcon,
      'size-row': sizeRow,
      'size-label': sizeLabel,
      'size-label-value': sizeLabelValue,
    } = this.els

    postLabel.element.textContent = postTitle
    if (postLabelValue) postLabelValue.element.textContent = postTitle

    // Use the post's picker image (post.image metafield) when set, otherwise
    // fall back to the first letter of the title.
    if (postImage) {
      const img = document.createElement('img')
      img.className = 'prd-DetailVariants_VariantImage'
      img.src = postImage
      img.alt = postTitle
      postIcon.element.replaceChildren(img)
    } else {
      postIcon.element.innerHTML = `<span>${postInitial}</span>`
    }

    this.clearSizeSelection()

    document.querySelectorAll('[data-post-size-list]').forEach(fieldset => {
      fieldset.hidden = fieldset.dataset.postSizeList !== postProductId
    })

    sizeRow.element.hidden = false
    sizeLabel.element.textContent = 'Please make a selection'
    if (sizeLabelValue) sizeLabelValue.element.textContent = ''

    this.updatePrice()

    if (window.drawers) window.drawers.close()
  }

  /**
   * @param {HTMLInputElement} radio
   */
  onSizeSelected (radio) {
    const variantId = radio.value
    const sizeTitle = radio.dataset.sizeTitle
    const {
      'size-label': sizeLabel,
      'size-label-value': sizeLabelValue,
    } = this.els

    sizeLabel.element.textContent = sizeTitle
    if (sizeLabelValue) sizeLabelValue.element.textContent = sizeTitle

    // Out-of-stock sizes stay selectable (so "Notify me" is reachable), but must
    // not enable add to bag — only stamp the line inputs when the size is in
    // stock, so updateSubmitButton keeps the button disabled otherwise.
    const available = radio.dataset.postAvailable !== 'false'

    const variantInput = this.variantInput
    if (variantInput) {
      variantInput.value = available ? variantId : ''
    }

    // VennApps needs the post line's product id for storefront API lookups, so
    // keep it in step with the variant id.
    const productInput = this.productInput
    if (productInput) {
      productInput.value = available ? radio.dataset.postProductId || '' : ''
    }

    this.updatePrice()
    this.updateSubmitButton()

    if (window.drawers) window.drawers.close()
  }

  /**
   * Computes the post's contribution to the displayed price (selected post
   * variant price minus the applicable post-group discount, clamped at 0) and
   * publishes it as a global addon. Dispatching `product-price:update` lets the
   * existing variant machinery re-render `main price * quantity + addon`.
   */
  updatePrice () {
    let addon = 0

    const checkedSize = document.querySelector(
      '[data-product-post-el="size-radio"]:checked',
    )
    const source =
      checkedSize ??
      document.querySelector('[data-product-post-el="post-radio"]:checked')

    if (source) {
      const price = Number(
        checkedSize
          ? source.dataset.postVariantPrice
          : source.dataset.postFromPrice,
      )
      const discount = Number(source.dataset.postDiscount)
      addon = Math.max(0, (price || 0) - (discount || 0))
    }

    document.body.dataset.postPriceAddon = String(addon)
    document.body.dispatchEvent(
      new CustomEvent('product-price:update', {
        bubbles: true,
        detail: { quantity: this.quantity },
      }),
    )
  }

  clearSizeSelection () {
    const variantInput = this.variantInput
    if (variantInput) variantInput.value = ''

    const productInput = this.productInput
    if (productInput) productInput.value = ''

    document
      .querySelectorAll('[data-product-post-el="size-radio"]')
      .forEach(radio => {
        radio.checked = false
      })

    this.updateSubmitButton()
  }

  updateSubmitButton () {
    const formId = this.dataset.formId
    if (!formId) return
    const button = document.querySelector(
      `#${formId} [data-product-add-to-cart]`,
    )
    if (!button) return

    // Mirror a normal variant: no size chosen shows the pending prompt; a chosen
    // but sold-out size disables add to bag and reads "Sold out"; an in-stock
    // size enables it.
    const checkedSize = document.querySelector(
      '[data-product-post-el="size-radio"]:checked',
    )
    const sizeChosen = !!checkedSize
    const sizeAvailable =
      sizeChosen && checkedSize.dataset.postAvailable !== 'false'
    const canAdd = sizeChosen && sizeAvailable

    button.setAttribute('aria-disabled', String(!canAdd))

    const textEl = button.querySelector('[data-product-add-to-cart-text]')
    if (textEl && button.dataset.addToCartText) {
      if (!sizeChosen) {
        textEl.textContent =
          button.dataset.postPendingText ?? button.dataset.addToCartText
      } else if (!sizeAvailable) {
        textEl.textContent =
          button.dataset.postSoldoutText ?? button.dataset.addToCartText
      } else {
        textEl.textContent = button.dataset.addToCartText
      }
    }
  }

  get variantInput () {
    const formId = this.dataset.formId
    if (!formId) return null
    return document.querySelector(
      `#${formId} [data-product-post-el="variant-id"]`,
    )
  }

  get productInput () {
    const formId = this.dataset.formId
    if (!formId) return null
    return document.querySelector(
      `#${formId} [data-product-post-el="product-id"]`,
    )
  }

  get quantity () {
    const formId = this.dataset.formId
    const form = formId ? document.getElementById(formId) : null
    const input = form?.querySelector(
      'input[name="items[0][quantity]"], input[name="quantity"]',
    )
    const value = input ? Number.parseInt(input.value, 10) : 1
    return Number.isNaN(value) || value < 1 ? 1 : value
  }
}

customElements.define('product-post', ProductPost)
